import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, CircularProgress, IconButton, InputAdornment, Paper } from '@mui/material';
import React, { useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import AIChatMessage from '../models/AIChatMessage';
import { Palette } from 'Palette';
import { CHAT_WINDOW } from 'DesignTokens';
import ChatMessageBubble from './ChatMessageBubble';
import { StyledButton } from './styles/StyledButtons';
import { StyledInput } from './styles/StyledInputs';
import { useChatAutoScroll } from './useChatAutoScroll';

interface GenericChatWindowProps {
  messages: AIChatMessage[];
  isProcessing: boolean;
  onSubmit: (input: string) => Promise<void>;
  onDocumentUpload?: (files: File[]) => void | Promise<void>;
  inputDisabled?: boolean;
  placeholderPrompt?: string;
  isUploadingFile?: boolean;
  /** Override the message area min/max height (e.g. "none" when parent already constrains height) */
  messageAreaHeight?: string | { min?: string; max?: string };
  /** Override the send button label (default: "Send") */
  submitLabel?: string;
  /**
   * Wrap the message list and the input row in a single bordered card (input
   * anchored at the bottom, inside it) instead of the message list having
   * its own border with the input sitting below as a separate element.
   * Opt-in — other call sites (Vera's floating chat, the prompt-preview
   * panel) keep their existing separate layout.
   */
  unifiedComposer?: boolean;
  /** Optional custom renderer for model messages (e.g. grounded citations). */
  renderModelContent?: (message: AIChatMessage) => React.ReactNode;
}

const GenericChatWindow: React.FC<GenericChatWindowProps> = ({
  messages,
  isProcessing,
  onSubmit,
  onDocumentUpload,
  inputDisabled = false,
  placeholderPrompt = 'Ask a question...',
  isUploadingFile = false,
  messageAreaHeight,
  submitLabel = 'Send',
  renderModelContent,
  unifiedComposer = false,
}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = React.useState('');

  const lastMessageSender = messages.length > 0 ? messages[messages.length - 1].role : null;

  const accentBg = CHAT_WINDOW.useBrandedColors ? Palette.default.buttonBg : '#1976d2';
  const accentText = CHAT_WINDOW.useBrandedColors ? Palette.default.buttonText : '#fff';
  const modelBubbleBg = CHAT_WINDOW.useBrandedColors ? Palette.tone1.background : '#f5f5f5';
  // Literal (not the sx `borderColor` theme-path shortcut) since this feeds a
  // raw `border` shorthand string below — 'divider' resolved is rgba(0,0,0,0.12).
  const modelBubbleBorderColor = CHAT_WINDOW.useBrandedColors
    ? Palette.tone1.border
    : 'rgba(0, 0, 0, 0.12)';
  const messageAreaBorderColor = CHAT_WINDOW.useBrandedColors ? Palette.default.border : 'divider';
  // A touch darker than the card's own border, and deliberately the same in
  // every state (default/hover/focus) rather than switching to MUI's
  // default blue on focus.
  const composerInputBorderColor = CHAT_WINDOW.useBrandedColors
    ? Palette.default.disabledText
    : undefined;

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (onDocumentUpload && acceptedFiles.length > 0) {
        onDocumentUpload(acceptedFiles);
      }
    },
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled: !onDocumentUpload,
  });

  const uploadInputProps = getInputProps({ multiple: true });

  useChatAutoScroll(messagesEndRef, inputRef, messages, isProcessing, isUploadingFile);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    onSubmit(inputValue.trim()).then(() => {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    });
    setInputValue('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...(unifiedComposer
          ? {
              // border-box so the 1px border is absorbed into the explicit
              // height:100% instead of adding 2px on top of it — otherwise
              // this card and a border-box sibling (e.g. the populated
              // fields panel) end up a couple of px out of sync.
              boxSizing: 'border-box',
              border: '1px solid',
              borderColor: messageAreaBorderColor,
              borderRadius: CHAT_WINDOW.messageAreaRadius,
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      {/* Chat messages area */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 1,
          minHeight: unifiedComposer
            ? 0
            : messageAreaHeight
              ? typeof messageAreaHeight === 'string'
                ? messageAreaHeight
                : (messageAreaHeight.min ?? 0)
              : CHAT_WINDOW.messageAreaMinHeight,
          maxHeight: unifiedComposer
            ? 'none'
            : messageAreaHeight
              ? typeof messageAreaHeight === 'string'
                ? messageAreaHeight
                : (messageAreaHeight.max ?? 'none')
              : CHAT_WINDOW.messageAreaMaxHeight,
          ...(unifiedComposer
            ? {}
            : {
                border: '1px solid',
                borderColor: messageAreaBorderColor,
                borderRadius: CHAT_WINDOW.messageAreaRadius,
              }),
        }}
        {...(onDocumentUpload ? getRootProps({ style: { outline: 'none' } }) : {})}
      >
        {onDocumentUpload && <input {...uploadInputProps} />}
        {messages.map((msg, idx) => (
          <ChatMessageBubble
            key={msg.timestamp?.toISOString() ?? `msg-${idx}`}
            message={msg}
            renderModelContent={renderModelContent}
          />
        ))}

        {isProcessing && lastMessageSender === 'user' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              p: 1.25,
              mb: CHAT_WINDOW.messageGap,
            }}
          >
            <Paper
              elevation={CHAT_WINDOW.bubbleElevation}
              sx={{
                p: 1.25,
                bgcolor: modelBubbleBg,
                border: `${CHAT_WINDOW.modelBubbleBorderWidth} solid ${modelBubbleBorderColor}`,
                borderRadius: CHAT_WINDOW.modelBubbleRadius,
                maxWidth: '65px',
                height: '30px',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
              }}
            >
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    animation: 'blink 1.4s infinite',
                    animationDelay: `${i * 0.2}s`,
                    '@keyframes blink': {
                      '0%, 80%, 100%': { opacity: 0 },
                      '40%': { opacity: 1 },
                    },
                  }}
                />
              ))}
            </Paper>
          </Box>
        )}

        {isUploadingFile && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              p: 1.25,
              mb: CHAT_WINDOW.messageGap,
            }}
          >
            <Paper
              elevation={CHAT_WINDOW.bubbleElevation}
              sx={{
                p: 1.25,
                bgcolor: accentBg,
                color: accentText,
                borderRadius: CHAT_WINDOW.userBubbleRadius,
                maxWidth: '120px',
                height: '30px',
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                Processing Upload...
              </Box>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    bgcolor: 'primary.contrastText',
                    animation: 'uploadBlink 1.2s infinite',
                    animationDelay: `${i * 0.15}s`,
                    '@keyframes uploadBlink': {
                      '0%, 80%, 100%': { opacity: 0.3 },
                      '40%': { opacity: 1 },
                    },
                  }}
                />
              ))}
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area + upload icon */}
      <Box
        className="chat-input-area"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          ...(unifiedComposer ? { p: 1 } : { mt: 2 }),
        }}
      >
        {onDocumentUpload && !CHAT_WINDOW.attachInsideInput && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation(); // prevent dropzone interference
              open();
            }}
            sx={{ flexShrink: 0 }}
          >
            <AttachFileIcon data-testid="AttachFileIcon" />
          </IconButton>
        )}

        <StyledInput
          inputRef={inputRef}
          fullWidth
          variant="outlined"
          size="small"
          placeholder={placeholderPrompt}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          InputProps={
            onDocumentUpload && CHAT_WINDOW.attachInsideInput
              ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          open();
                        }}
                        sx={{ flexShrink: 0, ml: -0.5 }}
                      >
                        <AttachFileIcon data-testid="AttachFileIcon" fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isProcessing && !inputDisabled) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={isProcessing || inputDisabled}
          sx={{
            minWidth: 0,
            flexShrink: 1,
            // StyledInput's own baked-in marginTop throws off centering
            // against the circular send button below; harmless no-op for
            // the unmodified non-admin text button, so only cancel it there.
            mt: CHAT_WINDOW.useCircularSendButton ? 0 : undefined,
            ...(unifiedComposer
              ? {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: CHAT_WINDOW.composerInputRadius,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: composerInputBorderColor,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: composerInputBorderColor,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: composerInputBorderColor,
                      borderWidth: '1px',
                    },
                  },
                }
              : {}),
          }}
        />

        {CHAT_WINDOW.useCircularSendButton ? (
          <IconButton
            aria-label={isProcessing ? 'Sending' : submitLabel}
            onClick={handleSend}
            disabled={isProcessing || inputDisabled || !inputValue.trim()}
            sx={{
              flexShrink: 0,
              width: CHAT_WINDOW.sendButtonSize,
              height: CHAT_WINDOW.sendButtonSize,
              bgcolor: accentBg,
              color: accentText,
              '&:hover': { bgcolor: accentBg },
              '&.Mui-disabled': {
                bgcolor: Palette.default.disabledBg,
                color: Palette.default.disabledText,
              },
            }}
          >
            {isProcessing ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <ArrowUpwardIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <StyledButton
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleSend}
            disabled={isProcessing || inputDisabled || !inputValue.trim()}
            endIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {isProcessing ? 'Sending' : submitLabel}
          </StyledButton>
        )}
      </Box>
    </Box>
  );
};

export default GenericChatWindow;
